// Lightweight, privacy-safe persistence + analytics hooks for the in-app
// guidance system (guided tours, help center, onboarding, workflow assistance).
//
// Design principles:
//   - NO personal data is ever stored or emitted. Only opaque, non-identifying
//     event names + a coarse role label + timestamps live here.
//   - All storage access is guarded so SSR / private-mode / disabled-storage
//     environments never throw into the React tree.
//   - Analytics is a no-op fan-out: events are buffered in-memory and (if a
//     global sink exists) forwarded. No network calls are made here.
//
// This module intentionally does not touch auth, tokens, apiClient, routing,
// or any geo-api endpoint.

export type GuidanceRole = 'admin' | 'investor' | 'operations' | 'gis' | 'mobile' | 'public';

const NS = 'giisdec.guidance.v1';

/* ──────────── safe storage helpers ──────────── */

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window?.localStorage?.getItem(`${NS}.${key}`);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window?.localStorage?.setItem(`${NS}.${key}`, JSON.stringify(value));
  } catch {
    /* ignore — guidance state is best-effort, never critical */
  }
}

/* ──────────── completion flags ──────────── */

type FlagMap = Record<string, boolean>;

export function hasSeenWelcome(role: GuidanceRole): boolean {
  const flags = readJson<FlagMap>('welcome', {});
  return Boolean(flags[role]);
}

export function markWelcomeSeen(role: GuidanceRole): void {
  const flags = readJson<FlagMap>('welcome', {});
  flags[role] = true;
  writeJson('welcome', flags);
  trackGuidance('onboarding_completed', { role });
}

export function resetWelcome(role: GuidanceRole): void {
  const flags = readJson<FlagMap>('welcome', {});
  delete flags[role];
  writeJson('welcome', flags);
}

export function hasCompletedTour(role: GuidanceRole): boolean {
  const flags = readJson<FlagMap>('tour', {});
  return Boolean(flags[role]);
}

export function markTourCompleted(role: GuidanceRole): void {
  const flags = readJson<FlagMap>('tour', {});
  flags[role] = true;
  writeJson('tour', flags);
  trackGuidance('tour_completed', { role });
}

export function resetTour(role: GuidanceRole): void {
  const flags = readJson<FlagMap>('tour', {});
  delete flags[role];
  writeJson('tour', flags);
}

/* ──────────── analytics fan-out (non-invasive) ──────────── */

export type GuidanceEvent =
  | 'tour_started'
  | 'tour_completed'
  | 'tour_dismissed'
  | 'help_viewed'
  | 'help_topic_viewed'
  | 'onboarding_started'
  | 'onboarding_completed'
  | 'workflow_guidance_viewed';

export interface GuidanceEventPayload {
  role?: GuidanceRole;
  topic?: string;
  stage?: string;
  ts?: number;
}

// Optional global sink — a host app (or test harness) can set
// window.__giisdecGuidanceSink to receive events. Never throws.
declare global {
  interface Window {
    __giisdecGuidanceSink?: (event: GuidanceEvent, payload: GuidanceEventPayload) => void;
  }
}

const buffer: { event: GuidanceEvent; payload: GuidanceEventPayload }[] = [];

export function trackGuidance(event: GuidanceEvent, payload: GuidanceEventPayload = {}): void {
  const enriched: GuidanceEventPayload = { ts: Date.now(), ...payload };
  try {
    buffer.push({ event, payload: enriched });
    if (buffer.length > 200) buffer.shift();
    if (typeof window !== 'undefined' && typeof window.__giisdecGuidanceSink === 'function') {
      window.__giisdecGuidanceSink(event, enriched);
    }
  } catch {
    /* analytics is best-effort only */
  }
}

export function getGuidanceEventBuffer(): ReadonlyArray<{ event: GuidanceEvent; payload: GuidanceEventPayload }> {
  return buffer.slice();
}
